/*
Available extended methods (super may be required!)
- refresh                       - runs when drupal is refreshed or can be run internally when required

Available methods
- onReady                       - runs when (min-width: 0rem) is hit
- onMobileNavInit               - runs when (max-width: 46.875rem) is hit
- onMobileNavDestroy            - runs when (max-width: 46.875rem) is hit

Available methods when Config.pluginswindowListenerPlugin.scroll == true
- scroll                        - runs every time scroll events are fired
- debouncedScroll               - runs every configured Xms to delay scroll events and improve performance
- delayedDebouncedScroll        - runs every time scroll events end firing after configured Xms

Available methods when Config.pluginswindowListenerPlugin.resize == true
- resize                        - runs every time resize events are fired
- debouncedResize               - runs every configured Xms to delay resize events and improve performance
- delayedDebouncedResize        - runs every time resize events end firing after configured Xms
*/

// imports
import MainApp from 'minsky-kit/js/managers/MainApp';
import { doc, getSupportInfo } from 'prettier';
import EmbedVideo from './minsky/components/EmbedVideo';
import Accordion from './minsky/components/Accordion';
import MenuOverlay from './minsky/components/MenuOverlay';
import Config from './Config'; // keep even though we don't use it
import SuggestionCarousel from './minsky/components/SuggestionCarousel';
import CalendarHover from './minsky/components/CalendarHover';
import ProgramView from './minsky/components/ProgramView';
import GalleryCarousel from './minsky/components/GalleryCarousel';
import InstanceManager from './minsky/managers/InstanceManager';
import ScrollHeader from './minsky/components/ScrollHeader';
import HeroParallax from './minsky/components/HeroParallax';
import Tabs from './minsky/components/Tabs';
import CookieConsent from './minsky/components/CookieConsent';
import DayTabs from './minsky/components/DayTabs';
import TicketSaleStart from './minsky/components/TicketSaleStart';
import CombinedMenus from './minsky/components/CombinedMenus';
import SearchResultsApp from './vue/apps/SearchResultsApp';

// class
export default class ProjectApp extends MainApp {
    // export default class ProjectApp extends MinskyApp {
    // constructor
    constructor(args = {}) {
        // set app instance debug state
        args.debug = true;

        // call super constructor
        super(args, 'Project App');

        // properties
        this._im = new InstanceManager();
        this.infoTabs = null;
        this.dayTabs = null;

        //
        this.scrollHeader = null;
    }

    // methods
    onReady() {
        this.log('App ready');

        document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
            anchor.addEventListener('click', function (e) {
                e.preventDefault();

                document.querySelector(this.getAttribute('href')).scrollIntoView({
                    behavior: 'smooth',
                });
            });
        });

        // video embeds
        this._im.initAddComponent('combined-menus', CombinedMenus, '.js-combined-menus');

        // cookies
        const $yodeck = document.querySelector('.yodeck');
        if (!$yodeck) {
            this.cookieConsent = new CookieConsent();
        }

        const $menuToggle = document.querySelector('.hamburger');
        const $menuContent = document.querySelector('.js-menu-content');
        if ($menuToggle && $menuContent) {
            const menuOverlay = new MenuOverlay({
                $el: {
                    content: $menuContent.innerHTML,
                },
                $trigger: $menuToggle,
            });
        }

        const $suggestions = document.querySelector('.suggestions__view .views-row');
        if ($suggestions) {
            const suggestionCarousel = new SuggestionCarousel({
                $el: $suggestions,
                settings: {
                    slidesPerView: 1,
                    preventClicks: false,
                    spaceBetween: 40,

                    breakpoints: {
                        550: {
                            slidesPerView: 2,
                        },
                        960: {
                            slidesPerView: 2.5,
                        },
                    },
                },
            });
        }

        const $calendar = document.querySelector('.calendar');
        if ($calendar) {
            new CalendarHover({
                $el: $calendar,
            });
        }

        const $concertTiming = document.querySelector('[data-start-sale]');
        if ($concertTiming) {
            new TicketSaleStart({
                $el: $concertTiming,
            });
        }

        const $today = document.querySelector('.today__inner');
        if ($today) {
            // fetch today's concert
            this.loading = true;
            const timestamp = +new Date();
            fetch(`/api/today?time=${timestamp}`)
                .then((r) => r.json())
                .then((data) => {
                    for (let i = 0; i < data.length; i++) {
                        const dom = document.createElement('div');
                        dom.innerHTML = data[i].html;
                        $today.appendChild(dom);
                    }
                })
                .catch((e) => {
                    this.error = true;
                    throw (e);
                })
                .finally(() => {
                    this.loading = false;
                });
        }

        const $program = document.querySelector('.program .calendar__view .views-element-container');
        const $archive = document.querySelector('.program--archive');
        if ($program) {
            new ProgramView({
                $el: $program,
                isArchive: $archive,
                filters: [
                    { key: 'type', type: 'checkbox', dropdown: true },
                    { key: 'genre', type: 'checkbox', dropdown: true },
                ],
            });
        }

        const $gallery = document.querySelector('.gallery--carousel .gallery__images');
        if ($gallery) {
            new GalleryCarousel({
                $el: $gallery,
                settings: {
                    width: 250,
                    spaceBetween: 20,
                    slidesOffsetAfter: 16,
                    slidesOffsetBefore: 16,
                    breakpoints: {
                        750: {
                            spaceBetween: 20,
                            slidesOffsetAfter: 40,
                            slidesOffsetBefore: 40,
                        },
                        1200: {
                            spaceBetween: 40,
                            slidesOffsetAfter: 50,
                            slidesOffsetBefore: 50,
                        },
                    },
                },
            });
        }

        const $pageHeader = document.querySelector('.page__header');
        if ($pageHeader) {
            this.libraries.load('gsap').then(() => {
                this.scrollHeader = new ScrollHeader({
                    $el: $pageHeader,
                });
            });
        }

        const $hero = document.querySelector('.hero');
        if ($hero) {
            this.libraries.load('gsap').then(() => {
                this.libraries.load('gsapScrollTrigger').then(() => {
                    new HeroParallax({
                        $el: $hero,
                    });
                });
            });
        }

        // festival day tabs
        const $tabsEl = document.querySelector('.tabs--days');
        const $triggers = document.querySelectorAll('.tabs--days .tabs__triggers .tabs__trigger');
        const $tabs = document.querySelectorAll('.tabs--days .tabs__content .tab');
        if ($triggers.length && $tabs.length) {
            const tabs = [];
            $tabs.forEach(($tab) => {
                const tab = {
                    trigger: [...$triggers].filter(($trigger) => $trigger.dataset.tabId === $tab.dataset.tabId)[0],
                    content: $tab,
                };
                tabs.push(tab);
            });

            this.libraries.load('gsap').then(() => {
                this.libraries.load('gsapScrollTo').then(() => {
                    this.dayTabs = new DayTabs({
                        $el: $tabsEl,
                        tabs,
                    });

                    this.dayTabs.on('tabChange', (e) => {
                        if (e.data.tabId && this.infoTabs) {
                            this.infoTabs.activate(e.data.tabId, true);
                        }
                    });
                });
            });
        }

        // festival info tabs
        const $infoTabsEl = document.querySelector('.tabs--days-info');
        const $infoTriggers = document.querySelectorAll('.tabs--days-info .festival-info__trigger');
        const $infoTabs = document.querySelectorAll('.tabs--days-info .festival-info__content');
        const $infoTriggersWrapper = document.querySelector('.tabs--days-info .tabs__triggers');
        const $infoContentWrapper = document.querySelector('.tabs--days-info .tabs__content');

        if ($infoTriggers.length && $infoTabs.length) {
            $infoTriggers.forEach((trigger) => {
                $infoTriggersWrapper.appendChild(trigger);
            });

            $infoTabs.forEach((tab) => {
                $infoContentWrapper.appendChild(tab);
            });

            const tabs = [];
            $infoTabs.forEach(($tab) => {
                const tab = {
                    trigger: [...$infoTriggers].filter(($trigger) => $trigger.dataset.tabId === $tab.dataset.tabId)[0],
                    content: $tab,
                };

                tabs.push(tab);
            });

            this.libraries.load('gsap').then(() => {
                this.libraries.load('gsapScrollTo').then(() => {
                    this.infoTabs = new Tabs({
                        $el: $infoTabsEl,
                        tabs,
                    });

                    this.infoTabs.on('tabChange', (e) => {
                        if (e.data.tabId && this.dayTabs) {
                            this.dayTabs.activate(e.data.tabId, true);
                        }
                    });
                });
            });
        }

        const $accordions = document.querySelectorAll('.accordion');
        if ($accordions.length) {
            this.libraries.load('gsap').then(() => {
                $accordions.forEach(($accordion) => {
                    const accordion = new Accordion({
                        $el: $accordion,
                    });
                });
            });
        }

        // scroll down arrow
        const $scrollCta = document.querySelector('.js-scroll-down');
        if ($scrollCta) {
            this.libraries.load('gsap').then(() => {
                this.libraries.load('gsapScrollTo').then(() => {
                    $scrollCta.addEventListener('click', () => {
                        gsap.to(window, {
                            duration: 1,
                            scrollTo: '#suggestions',
                        });
                    });
                });
            });
        }

        this._im.initAddComponent('search-results-app', SearchResultsApp, '#js-search-results-app', ($el) => ({
            $el,
            drupal: Drupal,
        }));


        // video embeds
        this._im.initAddComponent('embed-video', EmbedVideo, '.embed-video');

        //tracking for ticketlinks
        const links = Array.from(document.querySelectorAll('a[href*=ticketmatic]'));
        if (links.length) {
            window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments); }
            gtag('get', 'G-P91MWFDGNR', 'client_id', (id) => {
                if (id) {
                    links.forEach($link => {
                        $link.setAttribute('href', `${$link.getAttribute('href').replace('?', `?ref=${id}&`)}`);
                    })
                }
            });
        }
    }

    refresh() {
        this.log('App refresh');

        super.refresh();
    }

    resize() {
        this.log('App resize');
    }

    debouncedResize() {
        this.log('App debounced resize');
    }

    debouncedScroll() {
        if (this.scrollHeader) {
            this.scrollHeader.update();
        }

        if (window.sectionTracker) {
            window.sectionTracker.refresh();
        }
    }

    scroll() {
        this.log('App scroll');
    }

    onMobileNavInit() {
        this.log('Mobile');
    }

    onMobileNavDestroy() {
        this.log('Desktop');
    }
}

// utilities

// event handlers
